/**
 * Кухня из конструктора: секции сторон и разбор на детали.
 *
 * Сторона кухни — нижний и верхний ряд секций. Пока пользователь их не трогал, ряды
 * собирает автосборка (`autoKitchenSections`) так, как это делают кухонные мастера:
 * высокие колонны (холодильник, духовка) — по краям, мойка с посудомойкой рядом, плита —
 * не вплотную к мойке, между ними ящики и шкафы около 600 мм; верхние шкафы — над нижними.
 * Первая правка на схеме переводит сторону в явный список (`design.sections`): у каждой
 * секции свои ширина, глубина и высота, верхний ряд не зависит от нижнего, удаление
 * секции не меняет размеры остальных.
 *
 * Разбор (`kitchenParts`) превращает секции в бруски: корпус, цоколь, фасады с рамкой,
 * ручки, столешница, мойка, варка, вытяжка, верхние шкафы. Всё в метрах, центр кухни
 * в начале координат, задняя стена — в −Z, перед — к +Z, как у остальных моделей.
 * Из этих же секций рисуется чертёж на плане (`kitchenPlan`).
 */
import {
	KitchenModuleKind,
	KitchenUpperKind,
	KitchenLayout,
	KitchenSide,
	KitchenFacadeStyle,
	KitchenHandleStyle,
	KitchenDoorType,
	FrontHinge,
	UpperFront,
	KitchenCellFill,
} from "@/api";
import { FURNITURE_BODY_COLORS, FURNITURE_FRONT_COLORS, FURNITURE_SURFACE_COLORS } from "./colors";
import {
	DOOR_ANGLE,
	DRAWER_LEVELS,
	FLAP_ANGLE,
	KITCHEN,
	KITCHEN_FIXED_WIDTH,
	KITCHEN_UPPER_SUFFIX,
	LIFT_ANGLE,
	METERS_PER_MM,
} from "./constants";
import { shiftMotion, swingAngle, type MotionVec, type PartMotion } from "./motion";
import {
	KitchenPartMaterial,
	KitchenPartShape,
	type KitchenCell,
	type KitchenDesign,
	type KitchenFrontSettings,
	type KitchenModule,
	type KitchenPart,
	type KitchenRunId,
	type KitchenRunSections,
	type KitchenSection,
	type KitchenUpperModule,
	type KitchenUpperSection,
} from "./types";

export const KITCHEN_FRONT_COLORS = FURNITURE_FRONT_COLORS;

export const KITCHEN_BODY_COLORS = FURNITURE_BODY_COLORS;

export const KITCHEN_WORKTOP_COLORS = FURNITURE_SURFACE_COLORS;

export const KITCHEN_APRON_COLORS = FURNITURE_SURFACE_COLORS;

/** Глубина нижней секции вместе с фасадом по умолчанию, мм */
export const KITCHEN_LOWER_DEPTH = KITCHEN.bodyDepth + KITCHEN.front;

/** Высокие модули — от пола до верха кухни, без столешницы над ними */
export const isTallKitchenModule = (kind: KitchenModuleKind) =>
	kind === KitchenModuleKind.Fridge || kind === KitchenModuleKind.Column;

/**
 * Высота высокого модуля, мм: своя у секции, иначе у холодильника — высота из «Техники»,
 * у колонны — до верха кухни; выше верха кухни не бывает
 */
export const kitchenTallHeight = (
	design: KitchenDesign,
	kind: KitchenModuleKind,
	own?: number | null,
): number =>
	Math.min(
		design.height,
		own ??
			(kind === KitchenModuleKind.Fridge ? (design.fridgeHeight ?? design.height) : design.height),
	);

/** Секция с корпусом и фасадами, которую можно поделить на ряды */
export const isDivisibleKitchenSection = (kind: KitchenModuleKind | KitchenUpperKind) =>
	kind === KitchenModuleKind.Doors ||
	kind === KitchenModuleKind.Drawers ||
	kind === KitchenUpperKind.Cabinet;

/** Новая кухня: угловая, с техникой и верхними шкафами */
export const newKitchenDesign = (id: string): Omit<KitchenDesign, "updatedAt"> => ({
	id,
	name: "Моя кухня",
	layout: KitchenLayout.Corner,
	cornerSide: KitchenSide.Left,
	island: false,
	lengths: { back: 3000, left: 2200, right: 2200, island: 1800 },
	height: 2200,
	worktopHeight: null,
	upperBottom: null,
	islandOverhang: null,
	islandSideOverhang: null,
	uppers: true,
	fridge: true,
	fridgeHeight: KITCHEN.fridgeHeight,
	column: true,
	bottomOven: false,
	dishwasher: true,
	frontColor: KITCHEN_FRONT_COLORS.Шалфей,
	bodyColor: KITCHEN_BODY_COLORS.Белый,
	worktopColor: KITCHEN_WORKTOP_COLORS.Мрамор,
	apronColor: KITCHEN_APRON_COLORS.Мрамор,
	facadeStyle: KitchenFacadeStyle.Shaker,
	handleStyle: KitchenHandleStyle.Bar,
	defaultDoorType: KitchenDoorType.Left,
	defaultShelves: 1,
	sections: null,
});

/** Какие стороны есть у кухни этой формы (слева направо по обходу стен) */
export const kitchenRunIds = (design: KitchenDesign): KitchenRunId[] => {
	const walls: KitchenRunId[] =
		design.layout === KitchenLayout.Straight
			? ["back"]
			: design.layout === KitchenLayout.Corner
				? design.cornerSide === KitchenSide.Left
					? ["left", "back"]
					: ["back", "right"]
				: ["left", "back", "right"];
	return design.island ? [...walls, "island"] : walls;
};

const isSideRun = (run: KitchenRunId) => run === "left" || run === "right";

/** Где на стороне начинается нижний ряд: боковые — за углом, после нижних задней стены */
export const kitchenRunStart = (run: KitchenRunId) => (isSideRun(run) ? KITCHEN.baseDepth : 0);

/** Где начинается верхний ряд: у боковых — за верхними шкафами задней стены */
export const kitchenUpperRunStart = (run: KitchenRunId) =>
	isSideRun(run) ? KITCHEN.upperDepth : 0;

/** Длина стороны под нижние секции, мм */
export const kitchenRunLength = (design: KitchenDesign, run: KitchenRunId) =>
	Math.max(0, design.lengths[run] - kitchenRunStart(run));

/** Длина стороны под верхние секции, мм */
export const kitchenUpperRunLength = (design: KitchenDesign, run: KitchenRunId) =>
	Math.max(0, design.lengths[run] - kitchenUpperRunStart(run));

type Slot =
	| { fixed: KitchenModuleKind; optional?: boolean }
	| { flex: number; start: KitchenModuleKind.Doors | KitchenModuleKind.Drawers };

/** Что стоит на стороне и в каком порядке: техника на своих местах, между ней — гибкие участки */
const runTemplate = (design: KitchenDesign, run: KitchenRunId): Slot[] => {
	const fridge: Slot[] = design.fridge ? [{ fixed: KitchenModuleKind.Fridge, optional: true }] : [];
	const column: Slot[] = design.column ? [{ fixed: KitchenModuleKind.Column, optional: true }] : [];
	const sink: Slot[] = [
		{ fixed: KitchenModuleKind.Sink },
		...(design.dishwasher ? [{ fixed: KitchenModuleKind.Dishwasher, optional: true }] : []),
	];
	const cooktop: Slot[] = [
		{ flex: 1, start: KitchenModuleKind.Drawers },
		{ fixed: design.bottomOven ? KitchenModuleKind.Oven : KitchenModuleKind.Cooktop },
		{ flex: 1, start: KitchenModuleKind.Drawers },
	];
	if (run === "island") return [{ flex: 1, start: KitchenModuleKind.Drawers }];
	if (design.layout === KitchenLayout.Straight) {
		return [...fridge, { flex: 1, start: KitchenModuleKind.Doors }, ...sink, ...cooktop, ...column];
	}
	if (design.layout === KitchenLayout.Corner) {
		// Угол — у занятой боковой стены; колонны — у свободного конца задней стены
		if (run === "back") {
			const fromCorner: Slot[] = [
				{ flex: 1.2, start: KitchenModuleKind.Doors },
				...cooktop.slice(1),
				...column,
				...fridge,
			];
			return design.cornerSide === KitchenSide.Left ? fromCorner : fromCorner.toReversed();
		}
		return [
			{ flex: 1, start: KitchenModuleKind.Doors },
			...sink,
			{ flex: 1, start: KitchenModuleKind.Drawers },
		];
	}
	// П-образная: мойка у задней стены, варка слева, колонны — у свободных концов сторон
	if (run === "back")
		return [
			{ flex: 1, start: KitchenModuleKind.Doors },
			...sink,
			{ flex: 1, start: KitchenModuleKind.Doors },
		];
	if (run === "left") return [...cooktop, ...fridge];
	return [{ flex: 1, start: KitchenModuleKind.Doors }, ...column];
};

/** Гибкий участок — модулями около 600 мм, поровну, остаток — последнему */
const fillFlex = (
	length: number,
	start: KitchenModuleKind.Doors | KitchenModuleKind.Drawers,
): KitchenModuleKind[] => {
	const count = Math.max(1, Math.round(length / KITCHEN.module));
	const kinds: KitchenModuleKind[] = [];
	for (let i = 0; i < count; i++) {
		kinds.push(
			i % 2 === 0
				? start
				: start === KitchenModuleKind.Doors
					? KitchenModuleKind.Drawers
					: KitchenModuleKind.Doors,
		);
	}
	return kinds;
};

const splitWidths = (length: number, count: number) => {
	const base = Math.floor(length / count / 10) * 10;
	return Array.from({ length: count }, (_, i) =>
		i === count - 1 ? length - base * (count - 1) : base,
	);
};

/** Нижние модули стороны по шаблону: техника по местам, свободное — ящиками и шкафами */
const autoLowerRow = (
	design: KitchenDesign,
	run: KitchenRunId,
): { kind: KitchenModuleKind; width: number }[] => {
	const length = kitchenRunLength(design, run);
	const modules: { kind: KitchenModuleKind; width: number }[] = [];
	let slots = runTemplate(design, run);
	const fixedSum = () =>
		slots.reduce((sum, slot) => sum + ("fixed" in slot ? KITCHEN_FIXED_WIDTH[slot.fixed] : 0), 0);
	// Не влезает — сначала убираем посудомойку, потом колонну, потом холодильник
	for (const drop of [
		KitchenModuleKind.Dishwasher,
		KitchenModuleKind.Column,
		KitchenModuleKind.Fridge,
	] as const) {
		if (fixedSum() <= length) break;
		slots = slots.filter((slot) => !("fixed" in slot && slot.fixed === drop));
	}
	const free = Math.max(0, length - fixedSum());
	const flexSlots = slots.filter((slot): slot is Extract<Slot, { flex: number }> => "flex" in slot);
	// Участок короче модуля не ставим: его длина уходит соседям
	let active = flexSlots;
	while (active.length > 1) {
		const weights = active.reduce((sum, slot) => sum + slot.flex, 0);
		const smallest = active.reduce((min, slot) => (slot.flex < min.flex ? slot : min));
		if ((free * smallest.flex) / weights >= 300) break;
		active = active.filter((slot) => slot !== smallest);
	}
	const weights = active.reduce((sum, slot) => sum + slot.flex, 0);
	const shares = new Map<Slot, number>();
	let left = free;
	active.forEach((slot, index) => {
		const share =
			index === active.length - 1 ? left : Math.round((free * slot.flex) / weights / 10) * 10;
		shares.set(slot, share);
		left -= share;
	});

	for (const slot of slots) {
		if ("fixed" in slot) {
			modules.push({ kind: slot.fixed, width: KITCHEN_FIXED_WIDTH[slot.fixed] });
			continue;
		}
		const share = shares.get(slot) ?? 0;
		if (share <= 0) continue;
		const kinds = fillFlex(share, slot.start);
		splitWidths(share, kinds.length).forEach((width, i) => modules.push({ kind: kinds[i], width }));
	}
	// Совсем узкий остаток (короче 300 мм в автосборке) отдаём соседу
	for (let i = modules.length - 1; i >= 0; i--) {
		if (modules[i].width >= 300 || modules.length === 1) continue;
		const neighbor = modules[i - 1] ?? modules[i + 1];
		neighbor.width += modules[i].width;
		modules.splice(i, 1);
	}
	return modules;
};

/** Что висит над нижним модулем в автосборке */
const autoUpperKind = (kind: KitchenModuleKind): KitchenUpperKind =>
	isTallKitchenModule(kind) || kind === KitchenModuleKind.Empty
		? KitchenUpperKind.Empty
		: kind === KitchenModuleKind.Cooktop || kind === KitchenModuleKind.Oven
			? KitchenUpperKind.Hood
			: KitchenUpperKind.Cabinet;

/**
 * Секции стороны по автосборке: нижний ряд по шаблону, верхний — над ним (шкафы,
 * вытяжка над плитой, пусто над колоннами).
 */
export const autoKitchenSections = (
	design: KitchenDesign,
	run: KitchenRunId,
): KitchenRunSections => {
	const lowerRow = autoLowerRow(design, run);
	const lower: KitchenSection[] = lowerRow.map((module, index) => ({
		id: String(index),
		kind: module.kind,
		width: module.width,
		depth: null,
		height: null,
		front: null,
		cells: null,
	}));
	if (run === "island") return { lower, upper: [] };
	// Верхний ряд боковой стороны начинается раньше нижнего — первый шкаф шире на разницу
	const lead = kitchenRunStart(run) - kitchenUpperRunStart(run);
	const upper: KitchenUpperSection[] = lowerRow.map((module, index) => {
		const id = `${index}${KITCHEN_UPPER_SUFFIX}`;
		const width = module.width + (index === 0 ? lead : 0);
		// Над невысоким холодильником — шкаф до верха кухни
		const above =
			module.kind === KitchenModuleKind.Fridge
				? design.height - kitchenTallHeight(design, KitchenModuleKind.Fridge)
				: 0;
		if (above >= KITCHEN.aboveFridgeMin) {
			return {
				id,
				kind: KitchenUpperKind.Cabinet,
				width,
				depth: null,
				height: above,
				front: null,
				cells: null,
			};
		}
		return {
			id,
			kind: autoUpperKind(module.kind),
			width,
			depth: null,
			height: null,
			front: null,
			cells: null,
		};
	});
	return { lower, upper };
};

/** Секции стороны: заданные вручную или по автосборке */
export const kitchenSections = (design: KitchenDesign, run: KitchenRunId): KitchenRunSections =>
	design.sections?.[run] ?? autoKitchenSections(design, run);

/** Ключ секции в кухне — «сторона:id» */
export const kitchenSectionKey = (run: KitchenRunId, id: string) => `${run}:${id}`;

/** Нижний ряд стороны: модули по порядку, вплотную друг к другу */
const lowerModules = (design: KitchenDesign, run: KitchenRunId): KitchenModule[] => {
	let cursor = kitchenRunStart(run);
	return kitchenSections(design, run).lower.map((section) => {
		const module: KitchenModule = {
			key: kitchenSectionKey(run, section.id),
			kind: section.kind,
			start: cursor,
			width: section.width,
			depth: section.depth ?? KITCHEN_LOWER_DEPTH,
			...(isTallKitchenModule(section.kind)
				? { height: kitchenTallHeight(design, section.kind, section.height) }
				: section.height !== undefined && { height: section.height }),
			...(section.front && { front: section.front }),
			...(section.cells && { cells: section.cells }),
		};
		cursor += section.width;
		return module;
	});
};

/** Собранная кухня: нижние модули по сторонам */
export const kitchenRuns = (
	design: KitchenDesign,
): Partial<Record<KitchenRunId, KitchenModule[]>> =>
	Object.fromEntries(kitchenRunIds(design).map((run) => [run, lowerModules(design, run)]));

/** Верхний ряд стороны; без верхних шкафов и у острова — пусто */
const upperModules = (design: KitchenDesign, run: KitchenRunId): KitchenUpperModule[] => {
	if (!design.uppers || run === "island") return [];
	const { upperBottom } = kitchenHeights(design);
	let cursor = kitchenUpperRunStart(run);
	return kitchenSections(design, run).upper.map((section) => {
		const module: KitchenUpperModule = {
			key: kitchenSectionKey(run, section.id),
			kind: section.kind,
			start: cursor,
			width: section.width,
			depth: section.depth ?? KITCHEN.upperDepth,
			height: Math.min(section.height ?? design.height - upperBottom, design.height),
			...(section.front && { front: section.front }),
			...(section.cells && { cells: section.cells }),
		};
		cursor += section.width;
		return module;
	});
};

/** Собранная кухня: верхние шкафы по сторонам */
export const kitchenUppers = (
	design: KitchenDesign,
): Partial<Record<KitchenRunId, KitchenUpperModule[]>> =>
	Object.fromEntries(kitchenRunIds(design).map((run) => [run, upperModules(design, run)]));

/**
 * Ближний к основной кухне край острова, от задней стены, мм. Остров стоит в проходе
 * от задней стены; но если до боковых модулей остаётся меньше прохода — он уходит
 * за них, иначе между островом и боковой стороной не пройти.
 */
export const islandNear = (design: KitchenDesign) => {
	const near = KITCHEN.baseDepth + KITCHEN.aisle;
	const sides = (["left", "right"] as const).filter((side) => kitchenRunIds(design).includes(side));
	if (sides.length === 0) return near;
	const islandHalf = Math.min(design.lengths.island, design.lengths.back) / 2;
	const clearance = design.lengths.back / 2 - KITCHEN.baseDepth - islandHalf;
	if (clearance >= KITCHEN.aisle) return near;
	return Math.max(near, ...sides.map((side) => design.lengths[side] + KITCHEN.aisle));
};

/** Свес столешницы острова для стульев, мм */
export const islandOverhang = (design: KitchenDesign) =>
	design.islandOverhang ?? KITCHEN.islandDepth - KITCHEN.worktopDepth;

/** Свес столешницы острова по торцам, мм — не шире задней стены кухни */
export const islandSideOverhang = (design: KitchenDesign) =>
	Math.max(
		0,
		Math.min(
			design.islandSideOverhang ?? 0,
			(design.lengths.back - Math.min(design.lengths.island, design.lengths.back)) / 2,
		),
	);

/** Габарит кухни на плане: ширина вдоль задней стены, глубина от неё, высота, мм */
export const kitchenSize = (design: KitchenDesign) => {
	const runs = kitchenRunIds(design);
	const depth = Math.max(
		KITCHEN.worktopDepth,
		...(runs.includes("left") ? [design.lengths.left] : []),
		...(runs.includes("right") ? [design.lengths.right] : []),
		...(design.island ? [islandNear(design) + KITCHEN.worktopDepth + islandOverhang(design)] : []),
	);
	return { width: design.lengths.back, depth, height: design.height };
};

/**
 * Система координат стороны: `u` — вдоль стороны, `v` — от стены в комнату, мм.
 * Возвращает точку плана (x, z) от центра кухни.
 */
export const kitchenRunFrame = (design: KitchenDesign, run: KitchenRunId) => {
	const { width, depth } = kitchenSize(design);
	const x0 = -width / 2;
	const z0 = -depth / 2;
	switch (run) {
		case "back":
			return (u: number, v: number) => [x0 + u, z0 + v] as const;
		case "left":
			return (u: number, v: number) => [x0 + v, z0 + u] as const;
		case "right":
			return (u: number, v: number) => [-x0 - v, z0 + u] as const;
		case "island": {
			// Остров смотрит фасадами к основной кухне; спинка шкафов — к комнате, со свесом
			const islandStart = -Math.min(design.lengths.island, width) / 2;
			const back = z0 + islandNear(design) + KITCHEN.worktopDepth;
			return (u: number, v: number) => [islandStart + u, back - v] as const;
		}
	}
};

/**
 * Высоты кухни от пола, мм: верх столешницы и низ верхних шкафов. Верхние шкафы не ниже
 * 300 мм высотой, как бы низко их ни опустили, и не ближе 400 мм над столешницей — иначе
 * они налезут на рабочую зону.
 */
export const kitchenHeights = (design: KitchenDesign) => {
	const worktopTop = design.worktopHeight ?? KITCHEN.worktopTop;
	const upperBottom = Math.min(
		Math.max(design.upperBottom ?? KITCHEN.upperBottom, worktopTop + 400),
		design.height - 300,
	);
	return { worktopTop, upperBottom };
};

/** Сколько ящиков в модуле: заданное пользователем или три */
export const kitchenDrawerCount = (front?: KitchenFrontSettings | null) => {
	const count = front?.drawers ?? 3;
	return DRAWER_LEVELS[count] ? count : 3;
};

/** Сколько полок внутри шкафа: заданное у секции или общее по умолчанию (1) */
export const kitchenShelfCount = (design: KitchenDesign, front?: KitchenFrontSettings | null) =>
	front?.shelves ?? design.defaultShelves ?? 1;

/** Стиль фасада секции: заданный у секции или общий по умолчанию («shaker») */
export const kitchenFacadeStyle = (
	design: KitchenDesign,
	front?: KitchenFrontSettings | null,
): KitchenFacadeStyle => front?.facade ?? design.facadeStyle ?? KitchenFacadeStyle.Shaker;

/** Тип ручек кухни («bar» по умолчанию) */
export const kitchenHandleStyle = (design: KitchenDesign): KitchenHandleStyle =>
	design.handleStyle ?? KitchenHandleStyle.Bar;

/** Есть ли встроенная духовка снизу в этой секции */
export const hasLowerOven = (
	design: KitchenDesign,
	module: Pick<KitchenModule, "kind" | "front">,
) =>
	module.kind === KitchenModuleKind.Oven ||
	Boolean(module.front?.oven) ||
	(module.kind === KitchenModuleKind.Cooktop && Boolean(design.bottomOven));

/** Направления стороны в плане: `u` — вдоль, `v` — от стены в комнату, единичные */
const runAxes = (frame: ReturnType<typeof kitchenRunFrame>) => {
	const [ox, oz] = frame(0, 0);
	const [ux, uz] = frame(1, 0);
	const [vx, vz] = frame(0, 1);
	const u = [ux - ox, uz - oz] as const;
	const v = [vx - ox, vz - oz] as const;
	// Правая рука того, кто стоит лицом к фасадам, — в сторону роста u или нет
	return { u, v, rightIsHighU: u[0] * v[1] - u[1] * v[0] > 0 };
};

/**
 * Где петли одной дверцы модуля, если стоять к нему лицом. По умолчанию — у начала
 * стороны (ручка у конца), как раскладывает автосборка.
 */
export const kitchenFrontHinge = (
	design: KitchenDesign,
	run: KitchenRunId,
	front?: KitchenFrontSettings | null,
): FrontHinge => {
	if (front?.hinge !== undefined && front.hinge !== null) return front.hinge;
	if (design.defaultDoorType === KitchenDoorType.Left) return FrontHinge.Left;
	if (design.defaultDoorType === KitchenDoorType.Right) return FrontHinge.Right;
	return runAxes(kitchenRunFrame(design, run)).rightIsHighU ? FrontHinge.Left : FrontHinge.Right;
};

/** Какой способ открывания выбран для фасада секции (с учётом общих настроек и ширины) */
export const kitchenDoorType = (
	design: KitchenDesign,
	run: KitchenRunId,
	target: { width: number; front?: KitchenFrontSettings | null },
	isUpper = false,
): KitchenDoorType => {
	const { width, front } = target;
	if (front?.doorType !== undefined && front.doorType !== null) return front.doorType;
	if (isUpper && front?.upper === UpperFront.Lift) return KitchenDoorType.Lift;
	if (front?.hinge !== undefined && front.hinge !== null)
		return width > KITCHEN.singleDoorMax
			? KitchenDoorType.Double
			: front.hinge === FrontHinge.Left
				? KitchenDoorType.Left
				: KitchenDoorType.Right;
	const def = design.defaultDoorType;
	if (def !== undefined && def !== null && !(isUpper && def === KitchenDoorType.Drawer)) {
		if (
			(def === KitchenDoorType.Left || def === KitchenDoorType.Right) &&
			width > KITCHEN.singleDoorMax
		)
			return KitchenDoorType.Double;
		return def;
	}
	if (width > KITCHEN.singleDoorMax) return KitchenDoorType.Double;
	return kitchenFrontHinge(design, run, front) === FrontHinge.Left
		? KitchenDoorType.Left
		: KitchenDoorType.Right;
};

/** Брусок стороны по пределам u, v (мм) и высоте y (мм от пола) */
const boxIn = (
	frame: ReturnType<typeof kitchenRunFrame>,
	height: number,
	[u0, u1]: readonly [number, number],
	[v0, v1]: readonly [number, number],
	[y0, y1]: readonly [number, number],
): Pick<KitchenPart, "position" | "size"> => {
	const [ax, az] = frame(u0, v0);
	const [bx, bz] = frame(u1, v1);
	return {
		position: {
			x: ((ax + bx) / 2) * METERS_PER_MM,
			y: ((y0 + y1) / 2 - height / 2) * METERS_PER_MM,
			z: ((az + bz) / 2) * METERS_PER_MM,
		},
		size: {
			x: Math.abs(bx - ax) * METERS_PER_MM,
			y: (y1 - y0) * METERS_PER_MM,
			z: Math.abs(bz - az) * METERS_PER_MM,
		},
	};
};

/** Участок столешницы: от u0 до u1 и до какой глубины от стены, мм */
interface WorktopSegment {
	u0: number;
	u1: number;
	depth: number;
}

/** Столешница — участками между колоннами и пустыми местами; глубина — по самой глубокой секции */
const worktopSegments = (modules: KitchenModule[]): WorktopSegment[] => {
	const segments: WorktopSegment[] = [];
	let open = false;
	for (const module of modules) {
		if (isTallKitchenModule(module.kind) || module.kind === KitchenModuleKind.Empty) {
			open = false;
			continue;
		}
		const depth = Math.max(KITCHEN.worktopDepth, module.depth + KITCHEN.worktopNose);
		const last = segments.at(-1);
		if (open && last) {
			last.u1 = module.start + module.width;
			last.depth = Math.max(last.depth, depth);
		} else {
			segments.push({ u0: module.start, u1: module.start + module.width, depth });
		}
		open = true;
	}
	return segments;
};

/** Фасад, который рисуют замыкания `kitchenParts`: ключ модуля, правки и плоскость фасада */
interface FrontOwner {
	key: string;
	front?: KitchenFrontSettings | null;
	/** Пределы фасада от стены, мм: задняя и передняя плоскость */
	v: readonly [number, number];
}

/** Детали кухни для 3D: по модулям всех сторон, столешницы участками между колоннами */
export const kitchenParts = (design: KitchenDesign): KitchenPart[] => {
	const parts: KitchenPart[] = [];
	const H = design.height;
	const { gap, front, board, plinth, worktop } = KITCHEN;
	const { worktopTop, upperBottom } = kitchenHeights(design);
	const counterBottom = worktopTop - worktop;
	const frontCounts = new Map<string, number>();
	const handleStyle = kitchenHandleStyle(design);

	for (const run of kitchenRunIds(design)) {
		const frame = kitchenRunFrame(design, run);
		const box = (
			u: readonly [number, number],
			v: readonly [number, number],
			y: readonly [number, number],
			material: KitchenPartMaterial,
			module?: string,
			shadow = false,
		) =>
			parts.push({
				shape: KitchenPartShape.Box,
				...boxIn(frame, H, u, v, y),
				material,
				module,
				shadow,
				run,
			});
		const axes = runAxes(frame);
		const toRoom: MotionVec = [axes.v[0], 0, axes.v[1]];

		/** Полки в проёме [y0, y1] корпуса глубиной `depth` */
		const shelvesIn = (
			u0: number,
			u1: number,
			y0: number,
			y1: number,
			count: number,
			depth: number,
			module: string,
		) => {
			for (let s = 1; s <= count; s++) {
				const sy = y0 + ((y1 - y0) * s) / (count + 1);
				box(
					[u0 + board, u1 - board],
					[8, depth - 15],
					[sy - board / 2, sy + board / 2],
					KitchenPartMaterial.Body,
					module,
				);
			}
		};

		/** Корпус из плит (стенки, дно, крышка, спинка) и внутренние полки */
		const carcass = (
			u0: number,
			u1: number,
			y0: number,
			y1: number,
			depth: number,
			module: string,
			shelves = 0,
		) => {
			const body = KitchenPartMaterial.Body;
			box([u0, u0 + board], [0, depth], [y0, y1], body, module, true);
			box([u1 - board, u1], [0, depth], [y0, y1], body, module, true);
			box([u0 + board, u1 - board], [0, depth], [y0, y0 + board], body, module, true);
			box([u0 + board, u1 - board], [0, depth], [y1 - board, y1], body, module, true);
			box([u0 + board, u1 - board], [0, 8], [y0 + board, y1 - board], body, module, true);
			shelvesIn(u0, u1, y0 + board, y1 - board, shelves, depth, module);
		};

		/** Всё, что нарисует `draw`, движется как одна створка или ящик */
		const moving = (motion: PartMotion, draw: () => void) => {
			const start = parts.length;
			draw();
			// Номер фасада — по порядку внутри модуля: правка другого модуля его не сдвигает
			const module = parts[start]?.module ?? run;
			const index = frontCounts.get(module) ?? 0;
			frontCounts.set(module, index + 1);
			for (let i = start; i < parts.length; i++) {
				parts[i].motion = motion;
				parts[i].front = `${module}/${index}`;
			}
		};
		/** Дверца на вертикальных петлях у края `uHinge`, на передней плоскости `v` */
		const hinged = (uHinge: number, v: number, hingeHighU: boolean): PartMotion => {
			const [px, pz] = frame(uHinge, v);
			const along = hingeHighU ? -1 : 1;
			const from: MotionVec = [axes.u[0] * along, 0, axes.u[1] * along];
			return {
				kind: "hinge",
				axis: "y",
				pivot: [px * METERS_PER_MM, 0, pz * METERS_PER_MM],
				angle: swingAngle(from, toRoom, "y", DOOR_ANGLE),
			};
		};
		/** Створка на горизонтальной оси у кромки `y`: подъёмник (петли сверху) или откидная вниз */
		const flap = (y: number, v: number, lift: boolean): PartMotion => {
			const [px, pz] = frame(0, v);
			const axis = Math.abs(axes.u[0]) > 0.5 ? "x" : "z";
			const from: MotionVec = [0, lift ? -1 : 1, 0];
			return {
				kind: "hinge",
				axis,
				pivot: [px * METERS_PER_MM, (y - H / 2) * METERS_PER_MM, pz * METERS_PER_MM],
				angle: swingAngle(from, toRoom, axis, lift ? LIFT_ANGLE : FLAP_ANGLE),
			};
		};
		const slide: PartMotion = {
			kind: "slide",
			offset: [
				toRoom[0] * KITCHEN.drawerTravel * METERS_PER_MM,
				0,
				toRoom[2] * KITCHEN.drawerTravel * METERS_PER_MM,
			],
		};

		/** Фасад (гладкий, рамочный «шейкер» или витрина со стеклом) и ручка */
		const panel = (
			u0: number,
			u1: number,
			y0: number,
			y1: number,
			owner: FrontOwner,
			handle: "left" | "right" | "top" | "bottom" | "none",
		) => {
			const { key: module, v } = owner;
			const style = kitchenFacadeStyle(design, owner.front);
			const frameWidth = Math.min(55, (u1 - u0) / 5, (y1 - y0) / 5);
			const raised = [v[1], v[1] + 5] as const;
			const facade = KitchenPartMaterial.Front;

			if (style === KitchenFacadeStyle.Glass && frameWidth > 15 && y1 - y0 > 220) {
				// Рамка по периметру и прозрачное стекло внутри (через него видны полки)
				box([u0 + gap, u1 - gap], v, [y1 - gap - frameWidth, y1 - gap], facade, module);
				box([u0 + gap, u1 - gap], v, [y0 + gap, y0 + gap + frameWidth], facade, module);
				box(
					[u0 + gap, u0 + gap + frameWidth],
					v,
					[y0 + gap + frameWidth, y1 - gap - frameWidth],
					facade,
					module,
				);
				box(
					[u1 - gap - frameWidth, u1 - gap],
					v,
					[y0 + gap + frameWidth, y1 - gap - frameWidth],
					facade,
					module,
				);
				const midV = (v[0] + v[1]) / 2;
				box(
					[u0 + gap + frameWidth, u1 - gap - frameWidth],
					[midV - 2, midV + 2],
					[y0 + gap + frameWidth, y1 - gap - frameWidth],
					KitchenPartMaterial.Glass,
					module,
				);
			} else {
				box([u0 + gap, u1 - gap], v, [y0 + gap, y1 - gap], facade, module);
				if (style === KitchenFacadeStyle.Shaker && frameWidth > 15) {
					box([u0 + gap, u1 - gap], raised, [y1 - gap - frameWidth, y1 - gap], facade, module);
					box([u0 + gap, u1 - gap], raised, [y0 + gap, y0 + gap + frameWidth], facade, module);
					box(
						[u0 + gap, u0 + gap + frameWidth],
						raised,
						[y0 + gap + frameWidth, y1 - gap - frameWidth],
						facade,
						module,
					);
					box(
						[u1 - gap - frameWidth, u1 - gap],
						raised,
						[y0 + gap + frameWidth, y1 - gap - frameWidth],
						facade,
						module,
					);
				}
			}

			if (handle === "none" || handleStyle === KitchenHandleStyle.None) return;

			if (handleStyle === KitchenHandleStyle.Cutout) {
				// Фрезерованный вырез / профиль Gola у края фасада
				const cv = [v[1] - 2, v[1] + 2] as const;
				const cu = (u0 + u1) / 2;
				const span = Math.min(180, (u1 - u0) * 0.55);
				const cy = handle === "bottom" ? y0 + gap + 12 : y1 - gap - 12;
				box(
					[cu - span / 2, cu + span / 2],
					cv,
					[cy - 9, cy + 9],
					KitchenPartMaterial.Plinth,
					module,
				);
				return;
			}

			const hv = [v[1] + 5, v[1] + (handleStyle === KitchenHandleStyle.Knob ? 22 : 25)] as const;
			const mid = (y0 + y1) / 2;
			const metal = KitchenPartMaterial.Handle;
			if (handleStyle === KitchenHandleStyle.Knob) {
				const hu = handle === "left" ? u0 + 42 : handle === "right" ? u1 - 42 : (u0 + u1) / 2;
				const hy =
					handle === "top"
						? y1 - 45
						: handle === "bottom"
							? y0 + 45
							: Math.max(y0 + 55, Math.min(mid, y1 - 120));
				box([hu - 13, hu + 13], hv, [hy - 13, hy + 13], metal, module);
				return;
			}

			if (handle === "left" || handle === "right") {
				const hu = handle === "left" ? u0 + 45 : u1 - 45;
				const hy0 = Math.max(y0 + 40, Math.min(mid, y1 - 260));
				box([hu - 6, hu + 6], hv, [hy0, Math.min(y1 - 40, hy0 + 200)], metal, module);
			} else {
				const hy = handle === "top" ? y1 - 50 : y0 + 50;
				const span = Math.min(260, (u1 - u0) * 0.5);
				const cu = (u0 + u1) / 2;
				box([cu - span / 2, cu + span / 2], hv, [hy - 6, hy + 6], metal, module);
			}
		};

		/** Дверца на петлях: ручка у края напротив петель (или снизу у верхних шкафов) */
		const door = (
			u0: number,
			u1: number,
			y0: number,
			y1: number,
			owner: FrontOwner,
			hingeHighU: boolean,
			handleAt: "side" | "bottom" | "top",
		) =>
			moving(hinged(hingeHighU ? u1 : u0, owner.v[1], hingeHighU), () =>
				panel(
					u0,
					u1,
					y0,
					y1,
					owner,
					handleAt !== "side" ? handleAt : hingeHighU ? "left" : "right",
				),
			);

		/** Ящик: фасад с ручкой сверху и короб за ним — выезжают вместе */
		const drawer = (u0: number, u1: number, y0: number, y1: number, owner: FrontOwner) =>
			moving(slide, () => {
				panel(u0, u1, y0, y1, owner, "top");
				const top = Math.max(y0 + 60, y1 - 40);
				const bottom = y0 + gap + 20;
				const [b0, b1] = [u0 + 28, u1 - 28];
				const depth = owner.v[0];
				const body = KitchenPartMaterial.Body;
				box([b0, b0 + 12], [40, depth], [bottom, top], body, owner.key);
				box([b1 - 12, b1], [40, depth], [bottom, top], body, owner.key);
				box([b0, b1], [40, 52], [bottom, top], body, owner.key);
				box([b0, b1], [40, depth], [bottom, bottom + 12], body, owner.key);
			});

		/** Стопка выдвижных ящиков в проёме [y0, y1] */
		const drawerStack = (u0: number, u1: number, y0: number, y1: number, owner: FrontOwner) => {
			const h = y1 - y0;
			const levels = DRAWER_LEVELS[kitchenDrawerCount(owner.front)].map((share) => y0 + h * share);
			for (let i = 0; i < levels.length - 1; i++) {
				drawer(u0, u1, levels[i], levels[i + 1], owner);
			}
		};

		/** Встроенный духовой шкаф в нижнюю базу (с выдвижным ящиком снизу) */
		const lowerOven = (u0: number, u1: number, y0: number, y1: number, owner: FrontOwner) => {
			const { key, v } = owner;
			const drawerTop = y0 + 140;
			drawer(u0, u1, y0, drawerTop, owner);
			box([u0 + gap, u1 - gap], v, [drawerTop + gap, y1 - gap], KitchenPartMaterial.Steel, key);
			box(
				[u0 + 28, u1 - 28],
				[v[1], v[1] + 5],
				[drawerTop + 34, y1 - 95],
				KitchenPartMaterial.Glass,
				key,
			);
			// Панель управления и ручка духовки
			box(
				[u0 + 28, u1 - 28],
				[v[1], v[1] + 4],
				[y1 - 80, y1 - 20],
				KitchenPartMaterial.Plinth,
				key,
			);
			box(
				[u0 + 55, u1 - 55],
				[v[1] + 5, v[1] + 24],
				[y1 - 112, y1 - 100],
				KitchenPartMaterial.Steel,
				key,
			);
		};

		/** Узкая выдвижная бутылочница (карго с двумя корзинами) */
		const bottleRack = (u0: number, u1: number, y0: number, y1: number, owner: FrontOwner) =>
			moving(slide, () => {
				panel(u0, u1, y0, y1, owner, "top");
				const b0 = u0 + 24;
				const b1 = u1 - 24;
				const depth = owner.v[0] - 20;
				const steel = KitchenPartMaterial.Steel;
				for (const by of [y0 + 80, (y0 + y1) / 2 + 20]) {
					box([b0, b1], [40, depth], [by, by + 12], steel, owner.key);
					box([b0, b0 + 6], [40, depth], [by + 12, by + 95], steel, owner.key);
					box([b1 - 6, b1], [40, depth], [by + 12, by + 95], steel, owner.key);
					box([b0, b1], [40, 48], [by + 12, by + 95], steel, owner.key);
				}
			});

		/** Фасад проёма по способу открывания: выбранному у секции или общему */
		const renderOpening = (
			u0: number,
			u1: number,
			y0: number,
			y1: number,
			owner: FrontOwner,
			isUpper: boolean,
			forced?: KitchenDoorType,
		) => {
			const type =
				forced ?? kitchenDoorType(design, run, { width: u1 - u0, front: owner.front }, isUpper);
			if (type === KitchenDoorType.None) return;
			if (type === KitchenDoorType.Drawer && !isUpper) {
				drawerStack(u0, u1, y0, y1, owner);
				return;
			}
			if (type === KitchenDoorType.Lift) {
				moving(flap(y1 - gap, owner.v[1], true), () => panel(u0, u1, y0, y1, owner, "bottom"));
				return;
			}
			if (type === KitchenDoorType.Flap) {
				moving(flap(y0 + gap, owner.v[1], false), () => panel(u0, u1, y0, y1, owner, "top"));
				return;
			}
			const handleAt = isUpper ? "bottom" : "side";
			if (
				type === KitchenDoorType.Double ||
				(type === KitchenDoorType.Drawer && u1 - u0 > KITCHEN.singleDoorMax)
			) {
				const mid = (u0 + u1) / 2;
				door(u0, mid, y0, y1, owner, false, handleAt);
				door(mid, u1, y0, y1, owner, true, handleAt);
				return;
			}
			const hinge =
				type === KitchenDoorType.Left
					? FrontHinge.Left
					: type === KitchenDoorType.Right
						? FrontHinge.Right
						: kitchenFrontHinge(design, run, owner.front);
			door(u0, u1, y0, y1, owner, (hinge === FrontHinge.Right) === axes.rightIsHighU, handleAt);
		};

		/**
		 * Секция, поделённая на ряды: перегородки между рядами, в каждом — ящик, дверца
		 * (по способу открывания секции) или открытая ниша с полками. Верхний ряд — остаток.
		 */
		const renderCells = (
			u0: number,
			u1: number,
			y0: number,
			y1: number,
			owner: FrontOwner,
			cells: KitchenCell[],
			isUpper: boolean,
		) => {
			const inner = owner.v[0];
			let y = y0;
			cells.forEach((cell, index) => {
				const top = index === cells.length - 1 ? y1 : Math.min(y1, y + cell.height);
				if (top - y < 40) return;
				if (index > 0) {
					box(
						[u0 + board, u1 - board],
						[8, inner],
						[y - board / 2, y + board / 2],
						KitchenPartMaterial.Body,
						owner.key,
					);
				}
				if (cell.fill === KitchenCellFill.Drawer) {
					drawer(u0, u1, y, top, owner);
				} else {
					shelvesIn(u0, u1, y, top, cell.shelves ?? 0, inner, owner.key);
					if (cell.fill === KitchenCellFill.Door) {
						// У дверцы ряда — способ открывания секции; ящик или «без двери» — значит петли
						const type = kitchenDoorType(
							design,
							run,
							{ width: u1 - u0, front: owner.front },
							isUpper,
						);
						const swing: KitchenDoorType =
							type !== KitchenDoorType.None && type !== KitchenDoorType.Drawer
								? type
								: u1 - u0 > KITCHEN.singleDoorMax
									? KitchenDoorType.Double
									: kitchenFrontHinge(design, run, owner.front) === FrontHinge.Left
										? KitchenDoorType.Left
										: KitchenDoorType.Right;
						renderOpening(u0, u1, y, top, owner, isUpper, swing);
					}
				}
				y = top;
			});
		};

		const isIsland = run === "island";
		const lower = lowerModules(design, run);
		for (const module of lower) {
			if (module.kind === KitchenModuleKind.Empty) continue;
			const u0 = module.start;
			const u1 = module.start + module.width;
			const key = module.key;
			const body = module.depth - front;
			const owner: FrontOwner = { key, front: module.front, v: [body, module.depth] };
			const shelves = kitchenShelfCount(design, module.front);
			const doorType = module.front?.doorType;

			if (isTallKitchenModule(module.kind)) {
				const top = Math.min(H, module.height ?? H);
				if (module.kind === KitchenModuleKind.Fridge) {
					carcass(u0, u1, plinth, top, body, key, shelves);
					if (doorType !== KitchenDoorType.None) {
						const hinge =
							doorType === KitchenDoorType.Left
								? FrontHinge.Left
								: doorType === KitchenDoorType.Right
									? FrontHinge.Right
									: kitchenFrontHinge(design, run, module.front);
						const hingeHighU = (hinge === FrontHinge.Right) === axes.rightIsHighU;
						const split = Math.max(plinth + 300, top - KITCHEN.fridgeTopDoor);
						door(u0, u1, plinth, split, owner, hingeHighU, "side");
						door(u0, u1, split, top, owner, hingeHighU, "side");
					}
				} else {
					// Колонна: ящик снизу, духовка, СВЧ и шкаф сверху с полками
					const { columnDrawerTop, columnOvenTop, columnMicrowaveTop } = KITCHEN;
					const [v0, v1] = owner.v;
					const steel = KitchenPartMaterial.Steel;
					const glass = KitchenPartMaterial.Glass;
					carcass(u0, u1, plinth, top, body, key, 0);
					drawer(u0, u1, plinth, columnDrawerTop, owner);
					box(
						[u0 + gap, u1 - gap],
						[v0, v1],
						[columnDrawerTop + gap, columnOvenTop - gap],
						steel,
						key,
					);
					box(
						[u0 + 30, u1 - 30],
						[v1, v1 + 4],
						[columnDrawerTop + 40, columnOvenTop - 70],
						glass,
						key,
					);
					box(
						[u0 + 60, u1 - 60],
						[v1 + 4, v1 + 24],
						[columnOvenTop - 50, columnOvenTop - 38],
						steel,
						key,
					);
					if (top - columnMicrowaveTop > 150) {
						box(
							[u0 + gap, u1 - gap],
							[v0, v1],
							[columnOvenTop + gap, columnMicrowaveTop - gap],
							steel,
							key,
						);
						box(
							[u0 + 30, u0 + (u1 - u0) * 0.68],
							[v1, v1 + 4],
							[columnOvenTop + 35, columnMicrowaveTop - 35],
							glass,
							key,
						);
						box(
							[u0 + (u1 - u0) * 0.74, u1 - 30],
							[v1, v1 + 4],
							[columnOvenTop + 35, columnMicrowaveTop - 35],
							glass,
							key,
						);
						carcass(u0, u1, columnMicrowaveTop, top, body, key, shelves);
						renderOpening(u0, u1, columnMicrowaveTop, top, owner, true);
					} else if (top - columnOvenTop > 100) {
						renderOpening(u0, u1, columnOvenTop, top, owner, true);
					}
				}
				box([u0, u1], [0, body - 40], [0, plinth], KitchenPartMaterial.Plinth, key);
				continue;
			}

			// Нижний модуль: полый корпус с полками, цоколь, фасады по наполнению
			const divided = isDivisibleKitchenSection(module.kind) && (module.cells?.length ?? 0) > 1;
			const hasShelvesInside =
				module.kind === KitchenModuleKind.Doors ||
				module.kind === KitchenModuleKind.Sink ||
				(doorType !== undefined && doorType !== KitchenDoorType.Drawer);
			carcass(
				u0,
				u1,
				plinth,
				counterBottom,
				body,
				key,
				!divided && hasShelvesInside && !hasLowerOven(design, module) ? shelves : 0,
			);
			box([u0, u1], [0, body - 40], [0, plinth], KitchenPartMaterial.Plinth, key);

			if (divided && module.cells) {
				renderCells(u0, u1, plinth, counterBottom, owner, module.cells, false);
			} else if (hasLowerOven(design, module)) {
				lowerOven(u0, u1, plinth, counterBottom, owner);
			} else if (module.kind === KitchenModuleKind.Bottle) {
				bottleRack(u0, u1, plinth, counterBottom, owner);
			} else if (
				module.kind === KitchenModuleKind.Drawers ||
				module.kind === KitchenModuleKind.Cooktop
			) {
				if (doorType !== undefined && doorType !== KitchenDoorType.Drawer) {
					renderOpening(u0, u1, plinth, counterBottom, owner, false);
				} else {
					drawerStack(u0, u1, plinth, counterBottom, owner);
				}
			} else if (module.kind === KitchenModuleKind.Dishwasher && doorType === undefined) {
				// Дверца посудомойки откидывается вниз
				moving(flap(plinth + gap, owner.v[1], false), () =>
					panel(u0, u1, plinth, counterBottom, owner, "top"),
				);
			} else {
				renderOpening(u0, u1, plinth, counterBottom, owner, false);
			}

			if (module.kind === KitchenModuleKind.Sink) {
				const cu = (u0 + u1) / 2;
				const bowl = Math.min(500, module.width - 120);
				const sink = KitchenPartMaterial.Sink;
				box([cu - bowl / 2, cu + bowl / 2], [90, 500], [worktopTop - 2, worktopTop + 2], sink, key);
				box(
					[cu - bowl / 2 + 20, cu + bowl / 2 - 20],
					[110, 480],
					[worktopTop - 160, worktopTop - 2],
					sink,
					key,
				);
				const [fx, fz] = frame(cu, 50);
				parts.push({
					shape: KitchenPartShape.Cylinder,
					position: {
						x: fx * METERS_PER_MM,
						y: (worktopTop + 160 - H / 2) * METERS_PER_MM,
						z: fz * METERS_PER_MM,
					},
					size: { x: 0.014, y: 0.32, z: 0.014 },
					material: KitchenPartMaterial.Steel,
					module: key,
					run,
				});
				const spout = boxIn(
					frame,
					H,
					[cu - 10, cu + 10],
					[50, 180],
					[worktopTop + 300, worktopTop + 320],
				);
				parts.push({
					shape: KitchenPartShape.Box,
					...spout,
					material: KitchenPartMaterial.Steel,
					module: key,
					run,
				});
			}
			if (module.kind === KitchenModuleKind.Cooktop || module.kind === KitchenModuleKind.Oven) {
				const cu = (u0 + u1) / 2;
				const hobHalf = Math.min(280, (module.width - 60) / 2);
				box(
					[cu - hobHalf, cu + hobHalf],
					[40, 560],
					[worktopTop, worktopTop + 6],
					KitchenPartMaterial.Glass,
					key,
				);
			}
		}

		// Верхний ряд — свои секции, не связанные с нижними: шкафы, вытяжка, пустые места
		const uppers = upperModules(design, run);
		for (const module of uppers) {
			if (module.kind === KitchenUpperKind.Empty) continue;
			const u0 = module.start;
			const u1 = module.start + module.width;
			const key = module.key;
			const bottom = H - module.height;
			if (module.kind === KitchenUpperKind.Hood) {
				// Вытяжка: козырёк и короб до верха кухни
				const cu = (u0 + u1) / 2;
				const hoodBottom = bottom + 100;
				const steel = KitchenPartMaterial.Steel;
				box(
					[u0 + 10, u1 - 10],
					[0, Math.max(260, module.depth + 150)],
					[hoodBottom, hoodBottom + 70],
					steel,
					key,
					true,
				);
				box([cu - 140, cu + 140], [0, 260], [hoodBottom + 70, H], steel, key, true);
				continue;
			}
			const body = module.depth - front;
			const owner: FrontOwner = { key, front: module.front, v: [body, module.depth] };
			const divided = (module.cells?.length ?? 0) > 1;
			carcass(u0, u1, bottom, H, body, key, divided ? 0 : kitchenShelfCount(design, module.front));
			if (divided && module.cells) renderCells(u0, u1, bottom, H, owner, module.cells, true);
			else renderOpening(u0, u1, bottom, H, owner, true);
		}

		// Столешница — участками между высокими колоннами; у острова — со свесом
		const segments = worktopSegments(lower);
		for (const segment of segments) {
			const v = isIsland
				? ([-islandOverhang(design), segment.depth] as const)
				: ([0, segment.depth] as const);
			const side = isIsland ? islandSideOverhang(design) : 0;
			box(
				[segment.u0 - side, segment.u1 + side],
				v,
				[counterBottom, worktopTop],
				KitchenPartMaterial.Worktop,
				undefined,
				true,
			);
		}
		// Фартук у стены между столешницей и верхними шкафами — отдельный материал
		if (
			!isIsland &&
			uppers.some((module) => module.kind !== KitchenUpperKind.Empty) &&
			segments.length > 0
		) {
			const first = segments[0].u0;
			const last = segments[segments.length - 1].u1;
			box([first, last], [0, 6], [worktopTop, upperBottom], KitchenPartMaterial.Apron);
		}
	}
	return parts;
};

/** Чертёж кухни на плане — прямоугольники модулей и их наполнение, мм от центра кухни */
export interface KitchenPlanModule {
	kind: KitchenModuleKind;
	run: KitchenRunId;
	/** Углы модуля по стене: четыре точки (x, z) */
	outline: (readonly [number, number])[];
	/** Центр модуля и направление «от стены» — для мойки и конфорок */
	center: readonly [number, number];
	along: readonly [number, number];
	into: readonly [number, number];
	width: number;
}

/** На плане нижний модуль — со свесом столешницы, как и раньше (600 мм у типового) */
const planDepth = (depth: number) => depth + KITCHEN.baseDepth - KITCHEN_LOWER_DEPTH;

export const kitchenPlan = (design: KitchenDesign): KitchenPlanModule[] =>
	kitchenRunIds(design).flatMap((run) => {
		const frame = kitchenRunFrame(design, run);
		const [ox, oz] = frame(0, 0);
		const [ax, az] = frame(1, 0);
		const [ix, iz] = frame(0, 1);
		return lowerModules(design, run)
			.filter((module) => module.kind !== KitchenModuleKind.Empty)
			.map((module) => {
				const u0 = module.start;
				const u1 = module.start + module.width;
				const depth = planDepth(module.depth);
				return {
					kind: module.kind,
					run,
					outline: [frame(u0, 0), frame(u1, 0), frame(u1, depth), frame(u0, depth)],
					center: frame((u0 + u1) / 2, depth / 2),
					along: [ax - ox, az - oz] as const,
					into: [ix - ox, iz - oz] as const,
					width: module.width,
				};
			});
	});

/** Край верхних шкафов на плане — пунктир по их переду, мм от центра кухни */
export interface KitchenPlanUpper {
	run: KitchenRunId;
	line: readonly [readonly [number, number], readonly [number, number]];
}

export const kitchenPlanUppers = (design: KitchenDesign): KitchenPlanUpper[] =>
	kitchenRunIds(design).flatMap((run) => {
		const frame = kitchenRunFrame(design, run);
		return upperModules(design, run)
			.filter((module) => module.kind === KitchenUpperKind.Cabinet)
			.map((module) => ({
				run,
				line: [
					frame(module.start, module.depth),
					frame(module.start + module.width, module.depth),
				] as const,
			}));
	});

/**
 * Часть кухни — сторона гарнитура или остров, которую после «Разделить на части»
 * двигают отдельно. Габарит — по деталям этой стороны на плане; центр — от центра кухни, мм.
 */
export interface KitchenPiece {
	run: KitchenRunId;
	center: readonly [number, number];
	width: number;
	depth: number;
}

export const kitchenPieces = (design: KitchenDesign): KitchenPiece[] => {
	const parts = kitchenParts(design);
	return kitchenRunIds(design).flatMap((run) => {
		const own = parts.filter((part) => part.run === run);
		if (own.length === 0) return [];
		let minX = Infinity;
		let maxX = -Infinity;
		let minZ = Infinity;
		let maxZ = -Infinity;
		for (const part of own) {
			const { x, z } = part.position;
			const cylinder = part.shape === KitchenPartShape.Cylinder;
			const w = cylinder ? part.size.x * 2 : part.size.x;
			const d = cylinder ? part.size.x * 2 : part.size.z;
			minX = Math.min(minX, (x - w / 2) / METERS_PER_MM);
			maxX = Math.max(maxX, (x + w / 2) / METERS_PER_MM);
			minZ = Math.min(minZ, (z - d / 2) / METERS_PER_MM);
			maxZ = Math.max(maxZ, (z + d / 2) / METERS_PER_MM);
		}
		return [
			{
				run,
				center: [Math.round((minX + maxX) / 2), Math.round((minZ + maxZ) / 2)] as const,
				width: Math.round(maxX - minX),
				depth: Math.round(maxZ - minZ),
			},
		];
	});
};

/** Детали одной части кухни — со сдвигом так, чтобы центр части был в начале координат */
export const kitchenPieceParts = (design: KitchenDesign, run: KitchenRunId): KitchenPart[] => {
	const piece = kitchenPieces(design).find((p) => p.run === run);
	if (!piece) return [];
	const dx = -piece.center[0] * METERS_PER_MM;
	const dz = -piece.center[1] * METERS_PER_MM;
	return kitchenParts(design)
		.filter((part) => part.run === run)
		.map((part) => ({
			...part,
			position: { x: part.position.x + dx, y: part.position.y, z: part.position.z + dz },
			motion: shiftMotion(part.motion, dx, dz),
		}));
};

/** Ссылка на часть кухни в каталоге: «id кухни#сторона» */
export const KITCHEN_PIECE_SEPARATOR = "#";

export const splitKitchenPieceId = (
	id: string,
): { kitchenId: string; run: KitchenRunId | null } => {
	const [kitchenId, run] = id.split(KITCHEN_PIECE_SEPARATOR);
	return { kitchenId, run: (run as KitchenRunId | undefined) ?? null };
};

/** Детали всей кухни или одной её части */
export const kitchenPartsOf = (design: KitchenDesign, run: KitchenRunId | null): KitchenPart[] =>
	run ? kitchenPieceParts(design, run) : kitchenParts(design);

/** Столешница на плане: контур участка между колоннами, у острова — со свесом, мм от центра кухни */
export interface KitchenPlanWorktop {
	run: KitchenRunId;
	outline: (readonly [number, number])[];
}

export const kitchenWorktops = (design: KitchenDesign): KitchenPlanWorktop[] =>
	kitchenRunIds(design).flatMap((run) => {
		const frame = kitchenRunFrame(design, run);
		const side = run === "island" ? islandSideOverhang(design) : 0;
		return worktopSegments(lowerModules(design, run)).map(({ u0, u1, depth }) => {
			const v0 = run === "island" ? -islandOverhang(design) : 0;
			return {
				run,
				outline: [
					frame(u0 - side, v0),
					frame(u1 + side, v0),
					frame(u1 + side, depth),
					frame(u0 - side, depth),
				],
			};
		});
	});
